
import { DataGrid } from '../DataGrid';
import { assert, Dictionary } from '../Utils';

export interface ExpansionStateHandler {
  (isExpanded: boolean): void;
}

export interface RowExpansionStateHandler {
  (rowKey: string, isExpanded: boolean): void;
}

/**
 * Interface for nested row objects that feeds into the DataGrid.
 */
export interface NestedRow extends DataGrid.Row {

  /** If provided, should point to another valid row in the domain. */
  parentKey?: string;

  /** Zero means root level. */
  nestingLevel: number;

  /** Whether this row is expanded. Only applies to rows with children. */
  isExpanded?: boolean;

  /** Expansion state change handler function for this specific row. */
  expansionHandler?: ExpansionStateHandler;
}

export interface NestedRowItemInfo<T> {
  /** reference to the actual item */
  domainItem: T;

  /** if item is a parent, this is how many children it has; zero otherwise. */
  numChildren: number;

  /** index into NestedRowStore's items array */
  domainIndex: number;

  /** index into NestedRowStore's rows array. Will be -1 if it's not visible */
  gridRowIndex: number;
}

/**
 * Row store designed for nested rows.
 * It provides a getItems implementation, subclasses just need to provide implementation for a few abstract methods.
 *
 * Note: This row store supports only two levels of hierarchy.
 */
export abstract class NestedRowStore<T> implements DataGrid.RowStore {

  /**
   * GROUP: Members
   */

  /** reference to full domain of items. */
  private items: T[] = [];

  /** Helper structure for quick look ups using keys. */
  private itemInfoLookup: Dictionary<NestedRowItemInfo<T>> = {};

  /** Ideally this would be a Set<>. */
  private expandedRowKeys: Dictionary<boolean>;

  /** Handler that knows what to do when an attempt to change a row's expansion state happens. */
  protected rowExpansionHandler: RowExpansionStateHandler;

  /** The rows representing the visible items in the grid (contains top level items and children of expanded parents) */
  private rows: DataGrid.Row[] = [];

  /**
   * GROUP: Constructor
   */

  /**
   * @param {T[]} items - The full domain of items regardless of visibility (parent expand/collapse state).
   * @param {RowExpansionHandler} rowExpansionHandler - A function that handles attempts to change a row expansion state.
   */
  public constructor(items: T[], rowExpansionHandler: RowExpansionStateHandler, expandedRowKeys?: Dictionary<boolean>) {
    this.rowExpansionHandler = rowExpansionHandler;
    assert(!!this.rowExpansionHandler, 'NestedRowStore expects a non null/undefined value for rowExpansionHandler');

    this.expandedRowKeys = expandedRowKeys || {};

    this.setItems(items);
  }

  public setItems(items: T[]): void {
    this.items = items || [];
    this.rows = [];
    this.itemInfoLookup = {};

    // build the rows and the itemInfoLookup
    let rowIndex = 0;
    this.items.forEach((item, index) => {
      // create itemInfo for all domain items
      let itemInfo = this.createRowItemInfo(index);
      this.itemInfoLookup[this.getKey(item)] = itemInfo;

      // create a row if item is visible
      if (this.shouldBeVisible(item)) {
        this.rows.push(this.buildRowFromItem(item));
        itemInfo.gridRowIndex = rowIndex;
        rowIndex++;
      }
    });
  }

  private createRowItemInfo(domainIndex: number, rowIndex: number = -1): NestedRowItemInfo<T> {
    return {
      domainIndex: domainIndex,
      domainItem: this.items[domainIndex],
      numChildren: this.countChildrenOfItemAtIndex(this.items, domainIndex),
      gridRowIndex: rowIndex
    };
  }

  /**
   * GROUP: Public functions
   */

  /**
   * @param {number} start - inclusive, not guaranteed to be a positive number
   * @param {number} end - inclusive
   */
  public getItems(start: number, end: number, rowHandler: (rows: DataGrid.Row[]) => void): void {
    rowHandler(this.rows);
  }

  public getRowItemInfo(key: string): NestedRowItemInfo<T> {
    return this.itemInfoLookup[key];
  }

  private getRowItemInfoForRowIndex(rowIndex: number): NestedRowItemInfo<T> {
    const key = this.rows[rowIndex].key;
    return this.itemInfoLookup[key];
  }

  /**
   * @param {number} gridRowIndex - the row index within the grid
   * @returns {number} - the domain index of the item displayed at gridRowIndex.
   */
  public getDomainIndexFromGridRowIndex(gridRowIndex: number): number {
    assert(gridRowIndex >= 0 && gridRowIndex <= this.rows.length, 'visibleItemIndex needs to be a valid index');
    let row = this.rows[gridRowIndex];
    let itemInfo: NestedRowItemInfo<T> = this.itemInfoLookup[row.key];
    if (!!itemInfo) {
      return itemInfo.domainIndex;
    }
    return -1;
  }

  public getGridRowIndexFromDomainIndex(domainIndex: number): number {
    let item = this.items[domainIndex];
    let itemInfo = this.itemInfoLookup[this.getKey(item)];
    if (!!itemInfo) {
      return itemInfo.gridRowIndex;
    }
    return -1;
  }

  public setExpandedRows(keys: Dictionary<boolean>): NestedRowStore<T> {
    this.expandedRowKeys = keys;
    this.updateRowsForExpansion();
    return this;
  }

  /**
   * GROUP: Abstracts functions
   */

  /**
   * Implementor is fully responsible for constructing rows that are valid. This includes things like
   * making sure all the required fields are populated, keys are unique, and parentKey links are valid.
   * See {@link NestedRow} for more details.
   */
  protected abstract buildRowFromItem(item: T): NestedRow;

  /**
   * Implementation should return undefined if item does not have a parent.
   */
  public abstract getParentKey(item: T): string | undefined;

  /**
   * Return key should be unique per item.
   * Among other purposes, this key will be used for looking up parent and react perf optimization.
   */
  public abstract getKey(item: T): string;

  /**
   * Implementation is responsible for figuring out how many children belong to the item at the given index.
   */
  protected abstract countChildrenOfItemAtIndex(items: T[], itemIndex: number): number;

  /**
   * GROUP: Internal functions
   */

  protected isExpanded(item: T): boolean {
    return this.expandedRowKeys[this.getKey(item)] !== undefined;
  }

  protected isRootItem(item: T): boolean {
    const parentKey = this.getParentKey(item);
    return parentKey === undefined || parentKey === null;
  }

  protected isParentExpanded(item: T): boolean {
    let parentKey = this.getParentKey(item);
    return (!!parentKey) ? this.expandedRowKeys[parentKey] !== undefined : false;
  }

  protected shouldBeVisible(item: T): boolean {
    return (this.isRootItem(item) || this.isParentExpanded(item));
  }

  protected getNestingLevel(item: T): number {
    let nestingLevel = 0;
    let parentKey = this.getParentKey(item);

    while (parentKey) {
      nestingLevel++;
      let itemInfo = this.itemInfoLookup[parentKey];
      assert(!!itemInfo, 'no matching item found for the specified parentKey');
      let parentItem = itemInfo.domainItem;
      assert(!!parentItem, 'no matching item found for the specified parentKey');
      parentKey = this.getParentKey(parentItem);
    }

    return nestingLevel;
  }

  /**
   * Updates rows[] to account for expanded/collapsed items. Uses splice to add/remove child rows
   * and also updates the gridRowIndexes inside itemInfoLookup for all affected rows.
   */
  private updateRowsForExpansion(): void {
    let rowIndex = 0;
    let domainIndex = 0;
    let numDomainItems = this.items.length;
    while (domainIndex < numDomainItems) {
      let item = this.items[domainIndex];
      let key = this.getKey(item);
      assert(key === this.rows[rowIndex].key, 'current item key does not match the current row key.');
      let itemInfo: NestedRowItemInfo<T> = this.itemInfoLookup[key];
      assert(!!itemInfo, 'this item has not been inserted into the item info lookup.');

      if (this.isRootItem(item)) {
        itemInfo.gridRowIndex = rowIndex;
        if (itemInfo.numChildren > 0) {
          if (this.isRowExpanded(key)) {
            this.expandRowAtIndex(rowIndex);
            rowIndex += itemInfo.numChildren;
          } else {
            this.collapseRowAtIndex(rowIndex);
          }
          domainIndex += itemInfo.numChildren;
        }
      }
      domainIndex++;
      rowIndex++;
    }
    this.rows = this.rows.splice(0);
  }

  /**
   * Inserts the children of the given row into the rows array and updates their itemInfo
   */
  private expandRowAtIndex(rowIndex: number): void {
    if (this.childRowsExist(rowIndex)) {
      this.updateChildGridRowIndexes(rowIndex);
      return;
    }
    this.setRowIsExpanded(rowIndex, true);

    // update the corresponding itemInfoLookup elements and build child rows if necessary
    let childRows: NestedRow[] = [];
    const parentKey = this.rows[rowIndex].key;
    const itemInfo = this.itemInfoLookup[parentKey];
    for (let i = 0; i < itemInfo.numChildren; i++) {
      const childDomainItem: T = this.items[itemInfo.domainIndex + 1 + i];
      let childItemInfo: NestedRowItemInfo<T> = this.itemInfoLookup[this.getKey(childDomainItem)];
      assert(!!childItemInfo, 'the item has not been inserted into the item info lookup.');
      assert(parentKey === this.getParentKey(childDomainItem), 'the item is not a child of this parent.');
      childItemInfo.gridRowIndex = rowIndex + 1 + i;

      childRows.push(this.buildRowFromItem(childDomainItem));
    }

    // insert the children into the rows array
    if (childRows.length > 0) {
      // TFSID: 759339 - Maximum call stack depth exceed when search in groups dialog on web
      // we cannot use "this.rows.splice(rowIndex + 1, 0, ...childRows)" due to the ... calling apply
      // it turns it into "this.rows.splice.apply(this.rows, [rowIndex + 1, 0].concat(childRows))"
      // and apply turns it into this.splice(rowIndex+1,0,childRows[0],childRows[1],...,childRows[childRows.length - 1])
      // we then run out of stack space after we pass in > 125,000 or so items (at least thats aproximately the number for chrome)
      // working around it with slice + concat
      const insertionIndex = rowIndex + 1;
      this.rows = this.rows.slice(0, insertionIndex).concat(childRows, this.rows.slice(insertionIndex));
    }
  }

  private updateChildGridRowIndexes(parentRowIndex: number): void {
    const parentInfo = this.getRowItemInfoForRowIndex(parentRowIndex);
    for (let i = 0; i < parentInfo.numChildren; i++) {
      const childDomainItem: T = this.items[parentInfo.domainIndex + 1 + i];
      let childItemInfo: NestedRowItemInfo<T> = this.itemInfoLookup[this.getKey(childDomainItem)];
      assert(!!childItemInfo, 'the item has not been inserted into the item info lookup.');
      childItemInfo.gridRowIndex = parentRowIndex + 1 + i;
    }
  }

  /**
   * Removes the children of the given row from the rows array and updates their itemInfo
   */
  private collapseRowAtIndex(rowIndex: number): void {
    if (!this.childRowsExist(rowIndex)) {
      return;
    }
    this.setRowIsExpanded(rowIndex, false);

    // remove the children from rows
    let itemInfo = this.itemInfoLookup[this.rows[rowIndex].key];
    this.rows.splice(rowIndex + 1, itemInfo.numChildren as number);

    // update the children's gridRowIndexes in itemInfoLookup
    for (let i = 0; i < itemInfo.numChildren; i++) {
      let childDomainItem = this.items[itemInfo.domainIndex + 1 + i];
      let childItemInfo = this.itemInfoLookup[this.getKey(childDomainItem)];
      childItemInfo.gridRowIndex = -1;
    }
  }

  private childRowsExist(parentRowIndex: number): boolean {
    if (parentRowIndex >= this.rows.length - 1) {
      return false;
    }
    // child rows are always inserted at parentIndex + 1, so check the next row's parentKey to see if it's a child
    return this.rows[parentRowIndex].key === (this.rows[parentRowIndex + 1] as NestedRow).parentKey;
  }

  private setRowIsExpanded(rowIndex: number, isExpanded: boolean): void {
    let row: NestedRow = this.rows[rowIndex] as NestedRow;
    row.isExpanded = isExpanded;
    const newRow = { ...row };
    this.rows[rowIndex] = newRow;    // reassign reference so React knows to update the row
  }

  public isRowExpanded(rowKey: string): boolean {
    let hasKey = this.expandedRowKeys[rowKey] !== undefined;
    return hasKey && this.expandedRowKeys[rowKey] === true;
  }
}
